import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Flag, CalendarDays, Timer, Sparkles, Loader2, ArrowRight, Wand2,
} from 'lucide-react';
import { API, fmtDate } from '../../lib/planConstants';

const GROUP_ICON = { milestone: Flag, custom: CalendarDays, ai: Sparkles };

export default function HorizonWizard({ options, authHeader, onGenerating, gradeInfo }) {
  const [selected, setSelected] = useState(null);
  const [targetDate, setTargetDate] = useState('');
  const [months, setMonths] = useState('');
  const [years, setYears] = useState('');
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [recommending, setRecommending] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = (opt) => {
    setSelected(opt.key);
    setRecommendation(null);
    setTargetDate(opt.suggested_date || '');
    if (opt.key === 'ai_recommended') recommend();
  };

  const recommend = async () => {
    setRecommending(true);
    try {
      const r = await axios.post(`${API}/futurepath-plan/recommend-horizon`, { notes }, authHeader);
      setRecommendation(r.data.recommendation);
      setTargetDate(r.data.recommendation.target_date || '');
    } catch {
      toast.error('Could not get a recommendation, pick an endpoint instead.');
    } finally {
      setRecommending(false);
    }
  };

  const generate = async () => {
    if (!selected) return toast.error('Choose where your plan should end.');
    const body = { notes };
    if (selected === 'ai_recommended' && recommendation) {
      body.endpoint_type = recommendation.endpoint_type;
      body.target_date = recommendation.target_date;
      body.custom_label = recommendation.label;
      body.source = 'ai';
    } else if (selected === 'duration') {
      body.endpoint_type = 'duration';
      body.months = Number(months) || 0;
      body.years = Number(years) || 0;
      body.custom_label = label || null;
    } else {
      body.endpoint_type = selected;
      body.target_date = targetDate || null;
      body.custom_label = label || null;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/futurepath-plan/generate`, body, authHeader);
      onGenerating();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start your plan.');
    } finally {
      setBusy(false);
    }
  };

  const needsDate = ['custom_date', 'custom_milestone'].includes(selected);
  const needsDuration = selected === 'duration';
  const isMilestone = options.find((o) => o.key === selected)?.group === 'milestone';

  return (
    <div className="space-y-6" data-testid="horizon-wizard">
      <div className="glass-card p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold">Step 1 · Planning horizon</p>
        <h2 className="text-lg md:text-lg font-semibold text-white mt-1">Where should your plan end?</h2>
        <p className="text-sm text-slate-300 mt-1">
          No caps, 6 months, four years, or all the way through licensing. Your endpoint sets the length of the plan.
          {gradeInfo?.graduation_year ? ` We have you graduating high school in ${gradeInfo.graduation_year}.` : ''}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
          {options.map((opt) => {
            const Icon = GROUP_ICON[opt.group] || Flag;
            const active = selected === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => pick(opt)}
                data-testid={`horizon-option-${opt.key}`}
                className={`text-left p-4 rounded-xl border transition-colors duration-200 ${
                  active
                    ? 'border-cyan-400/60 bg-cyan-500/10'
                    : 'border-slate-700/80 bg-slate-900/40 hover:border-cyan-500/40'
                }`}
              >
                <Icon className={`w-4 h-4 mb-2 ${active ? 'text-cyan-300' : 'text-slate-300'}`} />
                <p className="text-sm font-medium text-white leading-snug">{opt.label}</p>
                {opt.suggested_date && (
                  <p className="text-xs text-slate-300 mt-1">Suggested: {fmtDate(opt.suggested_date)}</p>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selected && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 space-y-4">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold">Step 2 · Details</p>

          {recommending && (
            <div className="flex items-center gap-2 text-sm text-slate-300">
              <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> Reading your profile to recommend an endpoint…
            </div>
          )}

          {recommendation && (
            <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-4" data-testid="ai-recommendation">
              <p className="text-sm font-semibold text-cyan-200 flex items-center gap-2">
                <Wand2 className="w-4 h-4" /> {recommendation.label}
              </p>
              <p className="text-xs text-slate-300 mt-1">Target: {fmtDate(recommendation.target_date)}</p>
              <p className="text-sm text-slate-300 mt-2">{recommendation.rationale}</p>
            </div>
          )}

          {(needsDate || isMilestone) && (
            <div>
              <label className="block text-xs text-slate-300 mb-1">Target date</label>
              <input
                type="date"
                value={targetDate ? String(targetDate).slice(0, 10) : ''}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2.5 px-3 text-white text-sm"
                data-testid="horizon-target-date"
              />
            </div>
          )}

          {needsDuration && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-300 mb-1">Years</label>
                <input type="number" min="0" value={years} onChange={(e) => setYears(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2.5 px-3 text-white text-sm"
                  data-testid="horizon-years" placeholder="e.g. 4" />
              </div>
              <div>
                <label className="block text-xs text-slate-300 mb-1">Months</label>
                <input type="number" min="0" value={months} onChange={(e) => setMonths(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2.5 px-3 text-white text-sm"
                  data-testid="horizon-months" placeholder="e.g. 6" />
              </div>
            </div>
          )}

          {(needsDate || needsDuration) && (
            <div>
              <label className="block text-xs text-slate-300 mb-1">Name this destination (optional)</label>
              <input value={label} onChange={(e) => setLabel(e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2.5 px-3 text-white text-sm"
                placeholder="e.g. Journeyman electrician license" data-testid="horizon-label" />
            </div>
          )}

          <div>
            <label className="block text-xs text-slate-300 mb-1">Anything the plan must account for? (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              className="w-full bg-slate-900 border border-slate-700 rounded-lg py-2.5 px-3 text-white text-sm"
              placeholder="Work schedule, family situation, a dream school, a certification you already started…"
              data-testid="horizon-notes" />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button onClick={generate} disabled={busy || recommending}
              className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
              data-testid="generate-plan-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Build my execution plan
            </button>
            {selected === 'ai_recommended' && !recommending && (
              <button onClick={recommend} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
                <Timer className="w-3.5 h-3.5" /> Recommend again
              </button>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
